import { Logger, Presenter } from 'wdc-cube'
import * as lang from 'src/utils/lang'
import FilterManager from './tcm_filter_manager'
import { TheConsumerMarketPresenter } from './tcm_presenter'
import { ChartEmpresasAtivasPorUfScope, type EmpresasAtivasPorUFBean } from './tcm_scopes'
import { StringKeywordOperator, TheConsumerMarketService, type EmpresaFilter } from './tcm_service'
import { type TCMTexts } from './tcm_texts'

const LOG = Logger.get('TcmPresenterForEmpresasAtivasPorUF')

const service = TheConsumerMarketService.singleton()

export default class TcmPresenterForEmpresasAtivasPorUF extends Presenter<
    ChartEmpresasAtivasPorUfScope,
    TheConsumerMarketPresenter
> {
    // :: Class methods

    constructor(owner: TheConsumerMarketPresenter) {
        super(owner, owner.scope.empresasAtivasPorUF)
        this.__parent = owner
        this.__texts = owner.texts
        this.__filterManager = owner.filterManager
    }

    // :: Instance

    private readonly __parent: TheConsumerMarketPresenter
    private readonly __texts: TCMTexts
    private readonly __filterManager: FilterManager

    // :: Public API

    async initialize() {
        this.scope.update = this.update
        this.scope.caption = this.__texts.totalEmpresasAtivasPorUF
        this.scope.onItemClicked = this.action(this.__onUfClicked)
        LOG.debug('ready')
    }

    override release(): void {
        super.release()
    }

    clear() {
        this.__filterManager.enderecoUfs.clear()
    }

    async fetch(filter: EmpresaFilter) {
        this.scope.loaded = false
        try {
            const resp = await service.fetch({
                total_de_empresas_ativas_por_uf: true,
                filter: filter
            })

            const dataset: EmpresasAtivasPorUFBean[] = []
            for (const bucket of resp.total_de_empresas_ativas_por_uf?.buckets ?? []) {
                dataset.push({
                    codigo: bucket.key,
                    quantidade: bucket.doc_count
                })
            }

            dataset.sort((a, b) => a.quantidade - b.quantidade)

            this.scope.dataset = dataset

            this.__markSelected()
        } finally {
            this.scope.loaded = true
        }
    }

    private __markSelected() {
        const codigoValMap = this.collectExplicitValues()

        for (const row of this.scope.dataset) {
            row.selected = codigoValMap.has(`${row.codigo}`)
        }
        this.scope.update()
    }

    private async __onUfClicked(cdUf: string) {
        const sCode = `${cdUf}`
        const filterPresenter = this.owner.filterPane.uf

        const inConditionScope = filterPresenter.getOrCreateIsIn()

        const map = this.collectExplicitValues()
        if (map.has(sCode)) {
            map.delete(sCode)
        } else {
            map.set(sCode, true)
        }

        inConditionScope.value = [...map.keys()]
        filterPresenter.publish()

        this.__markSelected()
        this.__parent.fetchData('totalDeEmpresaAtivasPorUf')
    }

    private collectExplicitValues() {
        const prop = this.__filterManager.enderecoUfs

        const codigoValMap = new Map<string, boolean>()
        for (const filterVal of prop.values()) {
            if (filterVal.value) {
                if (
                    filterVal.operator == StringKeywordOperator.is_in ||
                    filterVal.operator == StringKeywordOperator.is
                ) {
                    stractUfs(filterVal.value).forEach((codigo) => codigoValMap.set(codigo, true))
                    continue
                }

                if (
                    filterVal.operator == StringKeywordOperator.is_not_in ||
                    filterVal.operator == StringKeywordOperator.is_not
                ) {
                    stractUfs(filterVal.value).forEach((codigo) => codigoValMap.delete(codigo))
                    continue
                }
            }
        }
        return codigoValMap
    }
}

function stractUfs(val: string[] | string | undefined): string[] {
    if (!val) {
        return []
    }

    const parts = Array.isArray(val) ? val : lang.splitValues(val)

    return parts.map((part) => `${part}`.trim()).filter((part) => part.length > 0)
}
